import React, { useEffect, useRef, useState } from 'react';
import { contractService, CustomerContract } from '../../../services/contract/ContractService';

interface ContractUploadProps {
  customerId: string;
}

export const ContractUpload: React.FC<ContractUploadProps> = ({ customerId }) => {
  const [contract, setContract] = useState<CustomerContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const c = await contractService.getContract(customerId);
      if (!cancelled) {
        setContract(c);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Eingabe zuruecksetzen, damit dieselbe Datei erneut gewaehlt werden kann.
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    setError(null);
    const result = await contractService.uploadContract(customerId, file);
    if (result.success) {
      const c = await contractService.getContract(customerId);
      setContract(c);
    } else {
      setError(result.error || 'Upload fehlgeschlagen');
    }
    setBusy(false);
  };

  const handleDownload = async () => {
    if (!contract) return;
    setError(null);
    const url = await contractService.getDownloadUrl(contract.storagePath, contract.dateiname);
    if (url) {
      window.open(url, '_blank');
    } else {
      setError('Download-Link konnte nicht erstellt werden');
    }
  };

  const handleDelete = async () => {
    if (!contract) return;
    if (!window.confirm('Vertrag wirklich löschen?')) return;

    setBusy(true);
    setError(null);
    const result = await contractService.deleteContract(customerId, contract.storagePath);
    if (result.success) {
      setContract(null);
    } else {
      setError(result.error || 'Löschen fehlgeschlagen');
    }
    setBusy(false);
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: busy ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    opacity: busy ? 0.6 : 1
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
        Vertrag
      </label>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {loading ? (
        <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>Lade...</p>
      ) : contract ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            padding: '10px 12px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '6px'
          }}
        >
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div
              style={{
                fontSize: '14px',
                color: '#111827',
                fontWeight: 500,
                wordBreak: 'break-all'
              }}
            >
              {contract.dateiname}
            </div>
            {contract.hochgeladenAm && (
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                Hochgeladen am {formatDate(contract.hochgeladenAm)}
              </div>
            )}
          </div>
          <button type="button" onClick={handleDownload} disabled={busy} style={buttonStyle}>
            Herunterladen
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy} style={buttonStyle}>
            Ersetzen
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            style={{ ...buttonStyle, color: '#DC2626', borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
          >
            Löschen
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy} style={buttonStyle}>
          {busy ? 'Wird hochgeladen...' : 'Vertrag hochladen'}
        </button>
      )}

      {busy && contract && (
        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>Wird verarbeitet...</p>
      )}

      {error && <p style={{ fontSize: '12px', color: '#EF4444', margin: 0 }}>{error}</p>}

      {!loading && !contract && !busy && (
        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
          Beliebiges Dateiformat, max. 25 MB.
        </p>
      )}
    </div>
  );
};

export default ContractUpload;
